import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import apiClient from '../services/apiClient';
import Header from '../components/Header';
import { CircularProgress } from '@mui/material';
import DatePeriodSelector from '../components/DatePeriodSelector';
import CustomTable, { Column } from '../components/CustomTable';
import { format } from 'date-fns';
import EmptyState from '../components/EmptyState';
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

const Wrapper = styled.div`
	background-color: white;
	width: 100%;
	min-height: 100vh;
	padding: 30px 40px;
	box-sizing: border-box;
	@media (max-width: 768px) {
		padding: 20px 10px;
		padding-top: 0px;
	}
`;

const PageTitle = styled.h1`
	font-size: 24px;
	margin-bottom: 20px;
	color: #333;
`;

const FiltersContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 15px;
	margin-bottom: 20px;

	@media (max-width: 768px) {
		flex-direction: column;
	}
`;

const FilterSelect = styled.select`
	padding: 8px 12px;
	border-radius: 4px;
	border: 1px solid #ddd;
	background-color: white;
	font-size: 14px;
	min-width: 150px;

	&:focus {
		outline: none;
		border-color: #3a5bd8;
	}
`;

const FilterLabel = styled.label`
	display: flex;
	align-items: center;
	gap: 5px;
	font-size: 14px;
	color: #666;
`;

interface ActivityLog {
	id: number;
	user_id: number;
	user_name: string;
	user_email: string;
	shop_id: number;
	action_type: string;
	resource_type: string;
	resource_id: number | null;
	message: string;
	date_created: string;
}

export default function ActivityLogs() {
	const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
	const [fetchState, setFetchState] = useState<'loading' | 'success' | 'failed'>('loading');
	const [fromDate, setFromDate] = useState<string | null>(null);
	const [toDate, setToDate] = useState<string | null>(null);
	const [selectedUserId, setSelectedUserId] = useState<string>('');
	const [selectedActionType, setSelectedActionType] = useState<string>('');
	const [selectedResourceType, setSelectedResourceType] = useState<string>('');
	const [users, setUsers] = useState<{ id: number; name: string }[]>([]);

	// Define columns for the activity logs table
	const columns: Column[] = [
		{
			field: 'date_created',
			headerName: 'Date & Time',
			flex: 1,
			renderCell: (params) => <div>{format(new Date(params.value), 'MMM dd, yyyy h:mm a')}</div>,
		},
		{
			field: 'user_name',
			headerName: 'User',
			flex: 1,
			renderCell: (params) => (
				<div>
					{params.value} ({params.row.user_email})
				</div>
			),
		},
		{
			field: 'action_type',
			headerName: 'Action',
			flex: 0.7,
			renderCell: (params) => <div style={{ textTransform: 'capitalize' }}>{params.value}</div>,
		},
		{
			field: 'resource_type',
			headerName: 'Resource',
			flex: 0.7,
			renderCell: (params) => <div style={{ textTransform: 'capitalize' }}>{params.value}</div>,
		},
		{
			field: 'message',
			headerName: 'Details',
			flex: 2,
		},
	];

	// Fetch activity logs data
	const fetchActivityLogs = async () => {
		setFetchState('loading');

		try {
			// Build query parameters
			const params = new URLSearchParams();
			if (fromDate) params.append('from', fromDate);
			if (toDate) params.append('to', toDate);
			if (selectedUserId) params.append('userId', selectedUserId);
			if (selectedActionType) params.append('actionType', selectedActionType);
			if (selectedResourceType) params.append('resourceType', selectedResourceType);

			const response = await apiClient.get(`/activity-logs?${params.toString()}`);

			// Check if response.status or response.data.status is 'Success'
			if (response && response.status === 'Success') {
				setActivityLogs(response.data.logs);
				setFetchState('success');
			} else if (response && response.data && response.data.logs) {
				// The apiClient already extracts response.data, so we need to access logs directly
				setActivityLogs(response.data.logs);
				setFetchState('success');
			} else {
				console.error('Invalid response structure:', response);
				setFetchState('failed');
			}
		} catch (error) {
			console.error('Error fetching activity logs:', error);
			setFetchState('failed');
		}
	};

	// Fetch users for filter dropdown
	const fetchUsers = async () => {
		try {
			const response = await apiClient.get('/shops/users');

			if (response.data.status === 'Success') {
				setUsers(
					response.data.data.map((user: any) => ({
						id: user.id,
						name: `${user.firstname} ${user.surname}`,
					}))
				);
			}
		} catch (error) {
			console.error('Error fetching users:', error);
		}
	};

	// Handle date range change
	const handleDateRangeChange = (from: Date, to: Date, period: string) => {
		setFromDate(format(from, 'yyyy-MM-dd'));
		setToDate(format(to, 'yyyy-MM-dd'));
	};

	// Initialize data on component mount
	useEffect(() => {
		// Set initial dates to current month
		const today = new Date();
		const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
		const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

		setFromDate(format(firstDayOfMonth, 'yyyy-MM-dd'));
		setToDate(format(lastDayOfMonth, 'yyyy-MM-dd'));

		fetchActivityLogs();
		fetchUsers();
	}, []);

	// Refresh data when filters change
	useEffect(() => {
		fetchActivityLogs();
	}, [fromDate, toDate, selectedUserId, selectedActionType, selectedResourceType]);

	// if (!isOwner) {
	//   return (
	//     <Wrapper>
	//       <Header
	//         title="Activity Logs"
	//         headerComponents={[
	//           <DatePeriodSelector
	//             key="date-period-selector"
	//             onDateRangeChange={handleDateRangeChange}
	//             initialPeriod="This month"
	//           />
	//         ]}
	//       />
	//       <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
	//         <div style={{ textAlign: 'center' }}>
	//           <h2>Access Denied</h2>
	//           <p>Only shop owners can view activity logs.</p>
	//         </div>
	//       </div>
	//     </Wrapper>
	//   );
	// }

	return (
		<Wrapper>
			<Header
				title="Activity Logs"
				headerComponents={[
					<DatePeriodSelector
						key="date-period-selector"
						onDateRangeChange={handleDateRangeChange}
						initialPeriod="This month"
					/>,
				]}
			/>
			<PageTitle>Activity Logs</PageTitle>

			<FiltersContainer>
				<FilterLabel>
					<PersonIcon fontSize="small" />
					<FilterSelect value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)}>
						<option value="">All Users</option>
						{users.map((user) => (
							<option key={user.id} value={user.id}>
								{user.name}
							</option>
						))}
					</FilterSelect>
				</FilterLabel>

				<FilterLabel>
					<FilterAltIcon fontSize="small" />
					<FilterSelect value={selectedActionType} onChange={(e) => setSelectedActionType(e.target.value)}>
						<option value="">All Actions</option>
						<option value="login">Login</option>
						<option value="logout">Logout</option>
						<option value="create">Create</option>
						<option value="update">Update</option>
						<option value="delete">Delete</option>
						<option value="upload">Upload</option>
						<option value="export">Export</option>
						<option value="import">Import</option>
					</FilterSelect>
				</FilterLabel>

				<FilterLabel>
					<CategoryIcon fontSize="small" />
					<FilterSelect
						value={selectedResourceType}
						onChange={(e) => setSelectedResourceType(e.target.value)}
					>
						<option value="">All Resources</option>
						<option value="variant">Variants</option>
						<option value="inventory">Inventory</option>
						<option value="order">Orders</option>
						<option value="expense">Expenses</option>
						<option value="user">Users</option>
						<option value="shop">Shops</option>
						<option value="invitation">Invitations</option>
					</FilterSelect>
				</FilterLabel>
			</FiltersContainer>

			{fetchState === 'loading' ? (
				<div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
					<CircularProgress />
				</div>
			) : (
				<CustomTable
					columns={columns}
					objects={activityLogs}
					fetchState={fetchState}
					emptyStateComponent={() => (
						<EmptyState
							title="No activity logs found"
							description="There are no activity logs matching your search criteria."
						/>
					)}
				/>
			)}
		</Wrapper>
	);
}
